import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import type { InvestigationData } from './mockInvestigation';

const MAX_VISIBLE_FACTORS = 3;

interface RiskRecognitionInvestigationProps {
  data: InvestigationData;
}

const RiskRecognitionInvestigation = ({ data }: RiskRecognitionInvestigationProps) => {
  const { t } = useTranslation('notifications');

  const subtitle =
    data.confidence != null && data.confidence < 100
      ? t('risk_recognition.drawer.investigation_subtitle_confidence', { confidence: data.confidence })
      : t('risk_recognition.drawer.investigation_subtitle');

  const visibleFactors = data.factors.slice(0, MAX_VISIBLE_FACTORS);

  return (
    <section
      className={cn(
        'rounded-[18px] border border-transparent border-solid shadow-[0px_1px_3px_rgba(0,27,184,0.05),0px_1px_2px_-1px_rgba(0,27,184,0.1)] [background-clip:padding-box,border-box] [background-image:linear-gradient(#fff,#fff),linear-gradient(248.25deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)] [background-origin:border-box]',
        'flex shrink-0 flex-col gap-6 overflow-hidden pb-6'
      )}
    >
      <header
        className={cn(
          '[background-image:linear-gradient(0deg,rgba(255,255,255,0.95),rgba(255,255,255,0.95)),linear-gradient(248.25deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)]',
          'flex flex-col gap-1.5 px-6 py-6'
        )}
      >
        <Typography variant='text-lg-semibold' className='text-[#09090b]'>
          {t('risk_recognition.drawer.investigation_title')}
        </Typography>
        <Typography variant='text-md' className='text-[#1c2024]'>
          {subtitle}
        </Typography>
      </header>

      {visibleFactors.length > 0 && (
        <div className='flex flex-col gap-3 px-6'>
          <Typography variant='text-md-semibold' className='text-[#09090b]'>
            {t('risk_recognition.drawer.factors_title')}
          </Typography>

          <ul className='flex flex-col gap-3'>
            {visibleFactors.map((factor) => (
              <li key={factor.title} className='flex flex-col gap-0.5'>
                <div className='flex items-center gap-2 pl-1.5'>
                  <span aria-hidden className='h-1.5 w-1.5 shrink-0 rounded-full bg-[#18181b]' />
                  <Typography variant='text-md-medium' className='text-[#18181b]'>
                    {factor.title}
                  </Typography>
                </div>
                <Typography variant='text-md' className='text-[#60646c]'>
                  {factor.description}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.recommendations.length > 0 && (
        <div className='px-6'>
          <div className='flex flex-col gap-2.5 rounded-[14px] border border-[#fdbdbe] border-solid bg-[#f9f9fb] px-3.5 py-2.5'>
            <div className='flex items-center gap-3'>
              <Icon name='triangle-alert' size='md' className='shrink-0 text-[#dc2626]' aria-hidden />
              <Typography variant='text-md-semibold' className='text-[#dc2626]'>
                {t('risk_recognition.drawer.immediate_recommendation')}
              </Typography>
            </div>
            <Typography variant='text-md' className='text-[#09090b]'>
              {`“${data.recommendations[0]}”`}
            </Typography>
          </div>
        </div>
      )}
    </section>
  );
};

export default RiskRecognitionInvestigation;
